// client/src/components/ClientInvoices.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { firestore, storage } from '../../firebase';
import {
  doc,
  getDoc,
  collection,
  getDocs,
  onSnapshot,
  query,
  limit as fsLimit
} from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { ref, getDownloadURL, listAll, getMetadata, uploadBytes } from 'firebase/storage';
import '../CSS/Dashboard.css';
import { useNavigate } from 'react-router-dom';
import ClientNavbar from './ClientNavbar';
import { FaArrowLeft, FaUpload } from 'react-icons/fa';
import { sanitize } from '../../utils/sanitize';
import { listSharedForUserByCompany } from '../../utils/shareStore';

const PHOTOS_CACHE_KEY = 'userPhotos';
const PHOTOS_LASTSYNC_KEY = 'userPhotos_lastSync';
const COMPANY_UID_CACHE_KEY = 'acceptedCompanyUID';
const COMPANY_UID_LASTSYNC_KEY = 'acceptedCompanyUID_lastSync';
const SETTINGS_CACHE_KEY = 'clientSettings';
const INV_SCAN_TTL_MS = 15 * 60 * 1000;
// Firestore rules: list queries require limit <= 50
const INV_LIMIT_SCAN = 50;
const INV_CACHE_KEY = (email) => `inv_cache_${email}`;
const INV_KNOWN_COMPANIES_KEY = (email) => `inv_known_companies_${email}`;

const PHOTOS_TTL_MS = 30 * 60 * 1000;          // 30 min : comme ClientPromo
const COMPANY_UID_TTL_MS = 24 * 60 * 60 * 1000; // 24 h : on ne rescane pas 'entreprises' souvent
const MEMO_TTL_MS = 24 * 60 * 60 * 1000;        // mémo en mémoire alignée sur 24h

// Mémo module-scope : persiste tant que l’onglet/pack est chargé
const acceptedCompanyMemo = new Map(); // key: email, value: { uid, expiresAt }

// Helper: comparaison superficielle d’array d’objets par clé
const shallowEqualBy = (a, b, key) => {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i][key] !== b[i][key]) return false;
  }
  return true;
};

const ClientInvoices = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isCompanyLinked, setIsCompanyLinked] = useState(null);
  const [photos, setPhotos] = useState(() => {
    try {
      const raw = localStorage.getItem(PHOTOS_CACHE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  const fileInputRef = useRef(null);
  const syncingRef = useRef(false);
  const importBusyRef = useRef(false);
  const companyUnsubsRef = useRef({});
  const companyAcceptedRef = useRef({});
  const companyScanLockRef = useRef(false);
  const lastCompanyEmailRef = useRef('');

  // -------- Helpers cache --------
  const loadCachedPhotos = useCallback(() => {
    try {
      const raw = localStorage.getItem(PHOTOS_CACHE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {}
    return [];
  }, []);

  const saveCachedPhotos = useCallback((arr) => {
    try {
      localStorage.setItem(PHOTOS_CACHE_KEY, JSON.stringify(arr));
      localStorage.setItem(PHOTOS_LASTSYNC_KEY, String(Date.now()));
    } catch {}
  }, []);

  const loadCachedCompanyUid = useCallback(() => {
    try {
      const raw = localStorage.getItem(COMPANY_UID_CACHE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {}
    return null;
  }, []);

  const saveCachedCompanyUid = useCallback((uidValue) => {
    try {
      localStorage.setItem(COMPANY_UID_CACHE_KEY, JSON.stringify(uidValue));
      localStorage.setItem(COMPANY_UID_LASTSYNC_KEY, String(Date.now()));
    } catch {}
  }, []);

  const needRefresh = (key, ttl) => {
    const raw = localStorage.getItem(key);
    const last = Number(raw || 0);
    return !Number.isFinite(last) || Date.now() - last > ttl;
  };

  const fromCache = (key, ttl) => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      if (raw.startsWith('{') || raw.startsWith('[')) {
        const obj = JSON.parse(raw);
        if (ttl == null) return obj;
        if (!obj || typeof obj !== 'object' || typeof obj.ts !== 'number') return null;
        if (Date.now() - obj.ts > ttl) return null;
        return obj;
      }
      if (ttl == null) return raw;
      return raw;
    } catch {
      return null;
    }
  };

  const putCache = (key, payload) => {
    try {
      const value = typeof payload === 'string' ? payload : JSON.stringify({ ...payload, ts: Date.now() });
      localStorage.setItem(key, value);
    } catch {}
  };

  // -------- Récupération acceptedCompanyUID (hybride: mémo + cache + user doc + fallback scan) --------
  const getAcceptedCompanyUID = useCallback(
    async (email) => {
      if (!email) return null;

      // 0) Mémo in-memory (0 I/O)
      const m = acceptedCompanyMemo.get(email);
      if (m && m.expiresAt > Date.now()) {
        return m.uid || null;
      }

      // 1) Cache local 24h
      const auth = getAuth();
      const uid = auth.currentUser?.uid;
      if (!needRefresh(COMPANY_UID_LASTSYNC_KEY, COMPANY_UID_TTL_MS)) {
        const cached = loadCachedCompanyUid();
        if (cached !== undefined) {
          acceptedCompanyMemo.set(email, { uid: cached, expiresAt: Date.now() + MEMO_TTL_MS });
          return cached || null;
        }
      }

      try {
        // 2) Utiliser d’abord le champ du user si déjà en mémoire
        if (user?.acceptedCompanyUID !== undefined) {
          const val = user.acceptedCompanyUID || null;
          saveCachedCompanyUid(val);
          acceptedCompanyMemo.set(email, { uid: val, expiresAt: Date.now() + MEMO_TTL_MS });
          return val;
        }

        // 2bis) Lecture ciblée du user (1 read max) plutôt que de scanner 'entreprises'
        if (uid) {
          const uref = doc(firestore, 'users', uid);
          const usnap = await getDoc(uref);
          const udata = usnap.exists() ? usnap.data() : {};
          const val = udata?.acceptedCompanyUID || null;
          saveCachedCompanyUid(val);
          acceptedCompanyMemo.set(email, { uid: val, expiresAt: Date.now() + MEMO_TTL_MS });
          if (val !== null) return val;
        }

        // 3) Fallback: scan limité 1/jour (comportement équivalent à avant)
        const cRef = collection(firestore, 'entreprises');
        const snap = await getDocs(query(cRef, fsLimit(INV_LIMIT_SCAN)));
        let acceptedCompanyUID = null;
        snap.forEach((docSnap) => {
          const data = docSnap.data() || {};
          const invs = Array.isArray(data.invitations) ? data.invitations : [];
          const hit = invs.find((i) => i?.employeeEmail === email && i?.status === 'accepted');
          if (hit) acceptedCompanyUID = data.userId || null;
        });

        saveCachedCompanyUid(acceptedCompanyUID);
        acceptedCompanyMemo.set(email, { uid: acceptedCompanyUID, expiresAt: Date.now() + MEMO_TTL_MS });
        return acceptedCompanyUID;
      } catch {
        const cached = loadCachedCompanyUid() || null;
        acceptedCompanyMemo.set(email, { uid: cached, expiresAt: Date.now() + MEMO_TTL_MS });
        return cached;
      }
    },
    [loadCachedCompanyUid, saveCachedCompanyUid, user?.acceptedCompanyUID]
  );

  // -------- Sync différencielle des photos (SWr) --------
  const diffSyncPhotos = useCallback(
    async (force = false) => {
      const auth = getAuth();
      const uid = auth.currentUser?.uid;
      if (!uid || syncingRef.current) return;
      if (isCompanyLinked === false) return;

      // 1) Afficher immédiatement le cache local (évite tout appel)
      const cached = loadCachedPhotos();
      if (cached.length) {
        const sortedCached = cached.slice().sort((a, b) => b.timestamp - a.timestamp);
        setPhotos((prev) => (shallowEqualBy(prev, sortedCached, 'name') ? prev : sortedCached));
      }

      // 2) Respect TTL (stale-while-revalidate) → évite listAll à chaque vue
      if (!force && !needRefresh(PHOTOS_LASTSYNC_KEY, PHOTOS_TTL_MS) && cached.length) {
        return; // cache encore frais, pas de hit Storage maintenant
      }

      syncingRef.current = true;

      try {
        // 3) acceptedCompanyUID (cache 24h) pour filtrage pro/perso
        const acceptedCompanyUID = user?.email ? await getAcceptedCompanyUID(user.email) : null;
        const sharedDocs =
          acceptedCompanyUID ? await listSharedForUserByCompany(acceptedCompanyUID, uid) : [];
        const sharedMap = new Map();
        sharedDocs.forEach((d) => {
          const fname = d.fileName || d.name || (d.id?.split('__')[1] || '');
          if (!fname) return;
          const compUid = d.companyUid || acceptedCompanyUID || null;
          if (compUid) sharedMap.set(fname, compUid);
        });

        // 4) Liste du dossier utilisateur (noms uniquement)
        const photosRef = ref(storage, `${uid}/photos/`);
        const list = await listAll(photosRef);

        // 4.1) Si les noms sont identiques au cache → pas de metadata/URL
        const cachedNamesSorted = cached.map((p) => p.name).sort();
        const listedNames = list.items.map((it) => it.name).sort();
        const sameNames =
          cachedNamesSorted.length === listedNames.length &&
          cachedNamesSorted.every((v, i) => v === listedNames[i]);

        if (sameNames) {
          // on repousse juste le TTL (cache validé)
          localStorage.setItem(PHOTOS_LASTSYNC_KEY, String(Date.now()));
          return;
        }

        const names = new Set(list.items.map((it) => it.name));

        // 5) DIFF : ne récupère metadata/URL que pour les nouveaux objets
        const cachedMap = Object.fromEntries(cached.map((p) => [p.name, p]));
        const toAdd = list.items.filter((it) => !cachedMap[it.name]);
        const surviving = cached
          .filter((p) => names.has(p.name))
          .map((p) => ({
            ...p,
            category: sharedMap.get(p.name) || 'perso',
          }));

        // 6) Fetch metadata + URL uniquement pour les nouveaux
        const added = await Promise.all(
          toAdd.map(async (item) => {
            try {
              const [meta, url] = await Promise.all([getMetadata(item), getDownloadURL(item)]);
              const createdAt = meta.timeCreated || 0;
              const ts = createdAt ? new Date(createdAt).getTime() : 0;

              const rawTitle = meta.customMetadata?.title || 'Aucun titre';
              const category = sharedMap.get(item.name) || 'perso';
              const title = sanitize(rawTitle);
              return {
                name: sanitize(item.name),
                url,
                timestamp: ts,
                title,
                category,
              };
            } catch {
              return null;
            }
          })
        );

        // 7) Fusion, dédoublonnage, tri (set uniquement si ça change)
        const merged = [...surviving, ...added.filter(Boolean)]
          .filter((v, i, self) => i === self.findIndex((t) => t.name === v.name))
          .sort((a, b) => b.timestamp - a.timestamp);

        setPhotos((prev) => (shallowEqualBy(prev, merged, 'name') ? prev : merged));
        saveCachedPhotos(merged);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Sync photos erreur:', e);
        if (!cached.length) {
          // anti-spam basique
          if (!/permission|denied|unauthorized/i.test(String(e?.message || ''))) {
            alert('Impossible de récupérer les photos.');
          }
        }
      } finally {
        syncingRef.current = false;
      }
    },
    [
      getAcceptedCompanyUID,
      isCompanyLinked,
      loadCachedPhotos,
      saveCachedPhotos,
      user?.email,
    ]
  );

  // -------- Détection affiliation entreprise (même logique que ClientDocs/Settings) --------
  const stopCompanyListeners = useCallback(() => {
    Object.values(companyUnsubsRef.current).forEach((fn) => fn && fn());
    companyUnsubsRef.current = {};
    companyAcceptedRef.current = {};
  }, []);

  const recomputeCompanyLink = useCallback(() => {
    const has = Object.values(companyAcceptedRef.current).some(Boolean);
    setIsCompanyLinked(has);
  }, []);

  const attachCompanyListener = useCallback((companyId, email) => {
    if (!companyId || !email || companyUnsubsRef.current[companyId]) return;
    const compRef = doc(firestore, 'entreprises', companyId);
    companyUnsubsRef.current[companyId] = onSnapshot(
      compRef,
      (snap) => {
        const data = snap.data() || {};
        const invitations = Array.isArray(data.invitations) ? data.invitations : [];
        const accepted = invitations.some(
          (inv) => inv?.employeeEmail === email && inv?.status === 'accepted'
        );
        companyAcceptedRef.current[companyId] = accepted;
        recomputeCompanyLink();
      },
      (err) => console.error('company listener error:', err)
    );
  }, [recomputeCompanyLink]);

  const discoverCompanyLink = useCallback(async (email) => {
    if (!email) {
      stopCompanyListeners();
      setIsCompanyLinked(false);
      lastCompanyEmailRef.current = '';
      return false;
    }

    if (companyScanLockRef.current) {
      if (lastCompanyEmailRef.current === email) return Object.values(companyAcceptedRef.current).some(Boolean);
      companyScanLockRef.current = false;
    }

    stopCompanyListeners();
    lastCompanyEmailRef.current = email;

    const cachedSettings = fromCache(SETTINGS_CACHE_KEY, null);
    if (cachedSettings?.company?.id) {
      companyAcceptedRef.current[cachedSettings.company.id] = true;
      recomputeCompanyLink();
    }

    const cached = fromCache(INV_CACHE_KEY(email), INV_SCAN_TTL_MS);
    if (cached?.companies?.length) {
      if (typeof cached.hasCompany === 'boolean') {
        setIsCompanyLinked(cached.hasCompany);
      }
      cached.companies.forEach((id) => attachCompanyListener(id, email));
      if (!navigator.onLine || cached.hasCompany === true) return cached.hasCompany === true;
    }

    if (companyScanLockRef.current) return Object.values(companyAcceptedRef.current).some(Boolean);
    companyScanLockRef.current = true;

    try {
      const entreprisesRef = collection(firestore, 'entreprises');
      const snap = await getDocs(query(entreprisesRef, fsLimit(INV_LIMIT_SCAN)));
      const companies = new Set();
      let hasAccepted = false;

      snap.forEach((docSnap) => {
        const data = docSnap.data() || {};
        const arr = Array.isArray(data.invitations) ? data.invitations : [];
        const accepted = arr.some((inv) => inv?.employeeEmail === email && inv?.status === 'accepted');
        const pending = arr.some((inv) => inv?.employeeEmail === email && inv?.status === 'pending');
        if (accepted || pending) companies.add(docSnap.id);
        companyAcceptedRef.current[docSnap.id] = accepted;
        if (accepted) hasAccepted = true;
      });

      setIsCompanyLinked(hasAccepted);
      const out = { companies: Array.from(companies), hasCompany: hasAccepted };
      putCache(INV_CACHE_KEY(email), out);
      putCache(INV_KNOWN_COMPANIES_KEY(email), { companies: out.companies });
      out.companies.forEach((id) => attachCompanyListener(id, email));
      return hasAccepted;
    } catch (err) {
      console.error('company link scan error:', err);
      setIsCompanyLinked((prev) => (prev === null ? false : prev));
      return false;
    } finally {
      companyScanLockRef.current = false;
    }
  }, [attachCompanyListener, recomputeCompanyLink, stopCompanyListeners]);

  // -------- Auth + user (1 read) --------
  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        navigate('/');
        return;
      }

      const email = u.email || '';
      const linked = await discoverCompanyLink(email);
      if (!linked) {
        navigate('/clientdocs');
        return;
      }

      // 1 seule lecture du user (pas de listener temps réel)
      try {
        const uref = doc(firestore, 'users', u.uid);
        const usnap = await getDoc(uref);
        const udata = usnap.exists() ? usnap.data() : {};
        const acceptedCompanyUID = udata?.acceptedCompanyUID ?? null;

        const enriched = {
          ...udata,
          email: u.email || udata.email || null,
          pseudo: udata.pseudo || 'Moi',
          acceptedCompanyUID,
        };

        setUser(enriched);

        // Bonus: alimente cache + mémo si on l’a
        saveCachedCompanyUid(acceptedCompanyUID);
        if (enriched.email) {
          acceptedCompanyMemo.set(enriched.email, { uid: acceptedCompanyUID, expiresAt: Date.now() + MEMO_TTL_MS });
        }
      } catch {
        setUser({ email: u.email || null, pseudo: 'Moi', acceptedCompanyUID: null });
      }

      // sync initiale (cache affiché tout de suite, refresh si TTL dépassé)
      diffSyncPhotos(false);
    });
    return () => unsub();
  }, [navigate, diffSyncPhotos, discoverCompanyLink, saveCachedCompanyUid]);

  // -------- Redirect si perte d'affiliation (listener) --------
  useEffect(() => {
    if (isCompanyLinked === false) {
      navigate('/clientdocs');
    }
  }, [isCompanyLinked, navigate]);

  // -------- Cleanup listeners entreprises --------
  useEffect(() => () => stopCompanyListeners(), [stopCompanyListeners]);

  // -------- Refresh “retour sur l’onglet/page” & “online” --------
  useEffect(() => {
    const onVisible = () => {
      // Au retour sur la page, si TTL dépassé → refresh silencieux
      if (needRefresh(PHOTOS_LASTSYNC_KEY, PHOTOS_TTL_MS)) {
        diffSyncPhotos(false);
      }
    };
    const onOnline = () => {
      if (needRefresh(PHOTOS_LASTSYNC_KEY, PHOTOS_TTL_MS)) {
        diffSyncPhotos(false);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    window.addEventListener('online', onOnline);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      window.removeEventListener('online', onOnline);
    };
  }, [diffSyncPhotos]);

  // -------- Import d’une image (optimiste + resync) --------
  const handleImportClick = () => fileInputRef.current?.click();

  const handleImportInvoice = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Veuillez sélectionner un fichier image (PNG, JPG, HEIC, etc.)');
      event.target.value = null;
      return;
    }

    const auth = getAuth();
    const uid = auth.currentUser?.uid;
    if (!uid) {
      alert('Utilisateur non connecté.');
      event.target.value = null;
      return;
    }

    if (importBusyRef.current) return;
    importBusyRef.current = true;

    try {
      const rawName = file.name.replace(/\.[^/.]+$/, '');
      const cleanName = sanitize(rawName);
      const cleanFilename = sanitize(file.name);

      const sref = ref(storage, `${uid}/photos/${cleanFilename}`);
      await uploadBytes(sref, file, {
        customMetadata: {
          TakenByPseudo: sanitize(user?.pseudo || 'Moi'),
          title: cleanName,
          category: 'perso',
        },
      });

      // Affichage optimiste + cache immédiat
      const [meta, url] = await Promise.all([getMetadata(sref), getDownloadURL(sref)]);
      const ts = meta.timeCreated ? new Date(meta.timeCreated).getTime() : Date.now();
      const optimistic = {
        name: cleanFilename,
        url,
        timestamp: ts,
        title: cleanName,
        category: 'perso',
      };

      const current = loadCachedPhotos();
      const merged = [optimistic, ...current.filter((p) => p.name !== optimistic.name)]
        .sort((a, b) => b.timestamp - a.timestamp);
      setPhotos((prev) => (shallowEqualBy(prev, merged, 'name') ? prev : merged));
      saveCachedPhotos(merged);

      // Resync (force) pour capter d’éventuels écarts ou méta
      await diffSyncPhotos(true);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Erreur lors de l’import de la photo :', e);
      alert('Erreur lors de l’import de la photo.');
    } finally {
      importBusyRef.current = false;
      event.target.value = null;
    }
  };

  // -------- UI helpers --------
  const handleBackClick = () => navigate('/clientdocs');

  const handleViewPhoto = (photo) => {
    navigate('/clientphotoviewer', { state: { photo } });
  };

  const formatTitle = (title) => {
    if (!title) return 'AUCUN TITRE';
    const text = sanitize(String(title)).trim();
    if (text.length > 10) return (text.substring(0, 10) + '...').toUpperCase();
    return text.toUpperCase();
  };

  return (
    isCompanyLinked === null || isCompanyLinked === false ? null : (
      <div className="dashboard-container ci-page">
        <ClientNavbar />
        <style>{CI_STYLES}</style>

        <div className="ci-inner">
          <header className="eb-page-header">
            <div>
              <h1 className="eb-page-title ci-title">Factures</h1>
            </div>
            <div className="ci-actions">
              <button type="button" onClick={handleBackClick} className="eb-btn eb-btn--secondary">
                <FaArrowLeft aria-hidden="true" /> Retour
              </button>
              {/* Importer */}
              <button type="button" onClick={handleImportClick} className="eb-btn eb-btn--primary">
                <FaUpload aria-hidden="true" /> Importer
              </button>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleImportInvoice}
              />
            </div>
          </header>

          {photos.length > 0 ? (
            <div className="ci-grid">
              {photos.map((photo) => (
                <button
                  type="button"
                  key={photo.name}
                  className="ci-item"
                  onClick={() => handleViewPhoto(photo)}
                >
                  <span className="ci-thumb">
                    <img
                      src={photo.url}
                      alt={sanitize(photo.title) || 'Aucune description'}
                      loading="lazy"
                    />
                  </span>
                  <span
                    className="ci-caption"
                    style={{
                      color: photo.title && photo.title !== 'Aucun titre' ? 'var(--eb-text)' : 'var(--eb-subtle)',
                    }}
                  >
                    {formatTitle(photo.title)}
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <div className="ci-empty">Aucune facture pour le moment.</div>
          )}
        </div>
      </div>
    )
  );
};

const CI_STYLES = `
.dashboard-container.ci-page { align-items: stretch; }
.ci-inner { width: 100%; max-width: var(--eb-content-max); margin: 0 auto; }
.dashboard-container .ci-title { text-align: left; margin: 0; font-size: 26px; }
.ci-actions { display: flex; flex-wrap: wrap; gap: 10px; margin-left: auto; }
.ci-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 16px;
}
.ci-item {
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 0;
  text-align: left;
  cursor: pointer;
  overflow: hidden;
  background: var(--eb-surface);
  border: 1px solid var(--eb-border);
  border-radius: var(--eb-radius-lg);
  box-shadow: var(--eb-shadow-sm);
  transition: border-color .15s ease, box-shadow .15s ease;
}
.ci-item:hover { border-color: var(--eb-border-strong); box-shadow: var(--eb-shadow-md); }
.ci-item:focus-visible { outline: none; box-shadow: var(--eb-ring); }
.ci-thumb { display: block; aspect-ratio: 3 / 4; background: var(--eb-surface-2); border-bottom: 1px solid var(--eb-border); }
.ci-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
.ci-caption {
  display: block;
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ci-empty {
  padding: 40px 20px;
  text-align: center;
  color: var(--eb-muted);
  background: var(--eb-surface);
  border: 1px solid var(--eb-border);
  border-radius: var(--eb-radius-lg);
  box-shadow: var(--eb-shadow-sm);
}
@media (max-width: 600px) {
  .dashboard-container .ci-title { font-size: 22px; }
  .ci-actions { width: 100%; }
  .ci-actions .eb-btn { flex: 1 1 0; }
  .ci-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
}
`;

export default ClientInvoices;
